import { PrimaryButton, TextButton } from '../components/Button';
import { ChoiceButton } from '../components/ChoiceButton';
import { Screen } from '../components/Screen';
import { ToggleCard } from '../components/ToggleCard';
import { copy } from '../copy';
import { FAILURE_REASONS } from '../api/types';
import { findStopById } from '../state/route';
import { useAppStore } from '../state/store';

const NOTE_MAX = 500;

/** H1/H2 — Niet gelukt. Beide schakelaars staan uit; alleen "Niemand aanwezig" zet
 *  herbezorging aan (zichtbaar en terug te zetten). Factuur nooit automatisch. */
export function FailScreen() {
  const route = useAppStore((s) => s.route);
  const stopId = useAppStore((s) => s.stopId);
  const draft = useAppStore((s) => s.failDraft);
  const setDraft = useAppStore((s) => s.setFailDraft);
  const resetDraft = useAppStore((s) => s.resetFailDraft);
  const setFailResult = useAppStore((s) => s.setFailResult);
  const recordAction = useAppStore((s) => s.recordAction);
  const navigate = useAppStore((s) => s.navigate);

  const stop = route ? findStopById(route, stopId) : null;
  if (!stop) return null;

  const submit = () => {
    if (!draft.reason) return;
    const redeliver = draft.redeliver;
    void recordAction({
      type: 'stop.fail',
      request: {
        stopId: stop.id,
        failureReason: draft.reason,
        note: draft.note.trim().length > 0 ? draft.note.trim() : null,
        invoiceAnyway: draft.invoiceAnyway,
        // Amendement A-07.1: de schakelaar gaat als vlag mee; de server kiest de
        // eerstvolgende bezorgdag (A-04) en geeft die datum in de uitkomst terug.
        planRedelivery: redeliver ? true : null,
      },
      subject: stop.partnerName,
    }).then((item) => {
      setFailResult({
        stopId: stop.id,
        partnerName: stop.partnerName,
        stopOrder: stop.stopOrder,
        reason: draft.reason!,
        invoiceAnyway: draft.invoiceAnyway,
        redeliver,
        clientActionId: item.clientActionId,
      });
      resetDraft();
      navigate('failconfirm');
    });
  };

  return (
    <Screen
      name="fail"
      foot={
        <PrimaryButton
          disabledReason={draft.reason ? null : copy.fail.submitDisabled}
          onClick={submit}
        >
          {copy.fail.submit}
        </PrimaryButton>
      }
    >
      <div className="pad-x" style={{ paddingTop: 12 }}>
        <TextButton onClick={() => navigate('stop')}>{copy.fail.back(stop.stopOrder)}</TextButton>
      </div>

      <div className="pad-x" style={{ paddingTop: 12 }}>
        <h1 className="t-serif t-titel">{copy.fail.title}</h1>
        <p className="o-75" style={{ fontSize: 17, marginTop: 4, marginBottom: 0 }}>
          {copy.fail.question}
        </p>
      </div>

      <div className="stack stack--sm pad-x" style={{ paddingTop: 16 }}>
        {FAILURE_REASONS.map((reason) => (
          <ChoiceButton
            key={reason}
            label={copy.fail.reasons[reason]}
            selected={draft.reason === reason}
            onSelect={() =>
              setDraft({
                reason,
                // Bij "Niemand aanwezig" staat herbezorging voor (B11); de factuur nooit.
                redeliver: reason === 'noOnePresent' ? true : draft.redeliver,
              })
            }
          />
        ))}
      </div>

      <div className="pad-x" style={{ paddingTop: 16 }}>
        <div className="field">
          <label className="field__label" htmlFor="fail-note">
            {copy.fail.note}
          </label>
          <textarea
            id="fail-note"
            className="field__input"
            rows={1}
            maxLength={NOTE_MAX}
            style={{ resize: 'none', minHeight: 26 }}
            value={draft.note}
            onChange={(event) => setDraft({ note: event.target.value })}
          />
        </div>
      </div>

      <div className="stack stack--sm pad-x" style={{ paddingTop: 16, paddingBottom: 16 }}>
        <ToggleCard
          title={copy.fail.invoiceTitle}
          consequence={copy.fail.invoiceConsequence}
          checked={draft.invoiceAnyway}
          onChange={(next) => setDraft({ invoiceAnyway: next })}
        />
        <ToggleCard
          title={copy.fail.redeliverTitle}
          consequence={copy.fail.redeliverConsequence}
          checked={draft.redeliver}
          onChange={(next) => setDraft({ redeliver: next })}
        />
      </div>
    </Screen>
  );
}
